import type { CompetenceRecord } from "../../api/client";
import { Acknowledgement } from "../../components/Acknowledgement";
import { Callout } from "../../components/Callout";
import { DateField } from "../../components/DateField";
import { TextField } from "../../components/TextField";

/**
 * Screen 16 — confidentiality, impartiality, and the excluded clients.
 *
 * These two undertakings block, and they are the only declarations that do. An auditor who
 * will not give them cannot be allocated to an audit at all (Cl. 4.5, 5.2, 7.3), so a
 * record without them describes someone who cannot do the job. The AI accountability
 * declaration on the next screen is a different case and is handled differently.
 *
 * The exclusions are Cl. 5.2.5's two-year bar. Every row is checked against the client
 * organisation at allocation and a match blocks (assertion 5, M2-3) — which is why the
 * reason is required: it is what an assessor reads when they ask why an allocation was
 * refused.
 */
export function DeclarationsStep({
  record,
  set,
  onAdd,
  onSet,
  onRemove,
  errorFor,
}: {
  record: CompetenceRecord;
  set: (patch: Partial<CompetenceRecord>) => void;
  onAdd: () => void;
  onSet: (index: number, patch: Record<string, string>) => void;
  onRemove: (index: number) => void;
  errorFor: (field: string) => string | undefined;
}) {
  const exclusions = record.exclusions ?? [];

  return (
    <>
      <div className="card">
        <h2>Confidentiality and impartiality</h2>

        <Acknowledgement
          id="confidentiality"
          title="Confidentiality — Cl. 4.5 and 7.3."
          checked={record.confidentiality ?? false}
          error={errorFor("confidentiality")}
          onChange={(confidentiality) => set({ confidentiality })}
        >
          I will treat all information obtained in the course of an audit as confidential,
          and will not disclose it except as required by law or agreed with the client.
        </Acknowledgement>

        <Acknowledgement
          id="impartiality"
          title="Impartiality — Cl. 5.2 and 7.3."
          checked={record.impartiality ?? false}
          error={errorFor("impartiality")}
          onChange={(impartiality) => set({ impartiality })}
        >
          I will declare any situation that could present a conflict of interest, and I will
          not audit an organisation where my objectivity could reasonably be questioned.
        </Acknowledgement>
      </div>

      <div className="card">
        <h2>Excluded clients</h2>

        <Callout title="Cl. 5.2.5 bars you for two years.">
          List every organisation you may not audit — prior employment, management system
          consultancy delivered, family or financial interest. Complao checks this list
          against the client organisation at intake and blocks the allocation.
        </Callout>

        <div className="row" style={{ justifyContent: "flex-end", marginBottom: "10px" }}>
          <button type="button" className="btn sec sm" onClick={onAdd}>
            Add excluded organisation
          </button>
        </div>

        {exclusions.map((entry, index) => (
          <div key={index} className="plan-ed" style={{ marginBottom: "10px" }}>
            <div className="grid2">
              <TextField
                id={`exclusion-${index}-organisation`}
                label="Organisation"
                placeholder="Legal entity name"
                value={entry.organisation_name ?? ""}
                onChange={(organisation_name) => onSet(index, { organisation_name })}
                error={errorFor(`exclusions.${index}.organisation_name`)}
              />
              <DateField
                id={`exclusion-${index}-until`}
                label="Excluded until"
                value={entry.excluded_until ?? ""}
                onChange={(excluded_until) => onSet(index, { excluded_until })}
                error={errorFor(`exclusions.${index}.excluded_until`)}
              />
            </div>
            <TextField
              id={`exclusion-${index}-reason`}
              label="Reason"
              placeholder="e.g. Delivered gap analysis in 2025 — Cl. 5.2.5 two-year bar"
              value={entry.reason ?? ""}
              onChange={(reason) => onSet(index, { reason })}
              error={errorFor(`exclusions.${index}.reason`)}
            />
            {errorFor(`exclusions.${index}`) ? (
              <p className="err" role="alert">
                {errorFor(`exclusions.${index}`)}
              </p>
            ) : null}
            <div style={{ marginTop: "9px" }}>
              <button
                type="button"
                className="btn danger sm"
                onClick={() => onRemove(index)}
              >
                Remove
              </button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
